import { IconPinMarker24 } from '@warp-ds/icons/react/pin-marker-24';
import { IconPlaneTicket24 } from '@warp-ds/icons/react/plane-ticket-24';
import { IconMap24 } from '@warp-ds/icons/react/map-24';

import type { CityPlace, CountryPlace, PlaceWithExists } from '@/interfaces/place';
import { ConfigProvider } from 'shared/config/ConfigContext';
import { PopularCities } from 'shared/seo/warp/PopularCities';
import { PopularCountries } from 'shared/seo/warp/PopularCountries';
import { LinkTree } from 'shared/seo/warp/LinkTree';
import flightData from 'shared/seo/data/flight.json';

import { config, feConfig } from '@/config/index.js';
import {
    flightCities,
    flightCountries,
    flightMapping,
    flightOdCombinations,
    flightOdCombinationsLookup,
    flightRecommendations,
    getCountriesForDestination,
    getCountryInfo,
    iataToCity,
} from '@/data';
import { verticals } from '@/data/verticals';
import { OriginDestinationPage } from '@/components/OriginDestinationPage';
import { Recommendations } from '@/components/Recommendations';
import { cityLevel2Factory, flightSearchFactory } from '@/components/factory';
import { validSeoPath } from 'shared/util/formatting';
import { InfoBox } from '@/components/dynamic/InfoBox';
import { pageContentUrl, pageFor } from '@/lib/flightContent';

const lowfarePricesUrl = config.get('flight.lowfarePrices');
const flightTrendsUrl = config.get('flight.trends');
const currencyApiUrl = config.get('currency.api');

const updatePrices = (placesWithExists: PlaceWithExists[], prices: Record<number, number>) =>
    placesWithExists.map((placeWithExist) => {
        placeWithExist.place.price = prices[placeWithExist.place.iata] ?? null;

        if (placeWithExist.place.priceData) {
            placeWithExist.place.priceData.price = prices[placeWithExist.place.iata] ?? null;
        }

        return placeWithExist;
    });

type CheapOffer = {
    origin: string;
    destination: string;
    cheapOffer: {
        origin: string;
        destination: string;
        departureDate: string;
        returnDate?: string;
        price: number;
    };
};
type Trend = {
    first30?: number;
    last30?: number;
    percentChange?: number | null;
};

const fetchPrices = async (origin: CityPlace, destinations: PlaceWithExists[]) => {
    const destinationParams = destinations.map((destination) => `destination=${destination.place.airport}`).join('&');
    const response = await fetch(`${lowfarePricesUrl}/${origin.airport}?${destinationParams}`);

    if (response.status === 404) {
        return {};
    }

    if (!response.ok) {
        const message = `Failing to fetch prices for ${origin.city}`;
        console.log(message, response);
        throw new Error(message);
    }

    // From [{ origin: 'OSL', destination: 'MAN', cheapOffer: { ..., price: 277 }}, ...] to { MAN: 277, ... }
    const cheapOffers: Array<CheapOffer> = await response.json();
    const prices: Record<number, number> = cheapOffers.reduce((acc, item) => {
        acc[item.destination] = item.cheapOffer.price;
        return acc;
    }, {});

    return prices;
};

const fetchTrend = async (airport: string): Promise<Trend | undefined> => {
    const response = await fetch(`${flightTrendsUrl}/${airport}`);

    if (response.status === 404) {
        return {};
    }

    if (!response.ok) {
        const message = `Failing to fetch ranking for ${airport}`;
        console.log(message, response);
        throw new Error(message);
    }

    return await response.json();
};

const capitalizeFirstLetter = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
};

const fetchCurrencyAndRate = async (countryLevel2: string) => {
    const countryInfo = getCountryInfo(capitalizeFirstLetter(countryLevel2));
    const res = await fetch(currencyApiUrl);

    if (res.ok) {
        const data = await res.json();
        const currencyData = data.data[countryInfo?.currency] ?? null;
        if (currencyData) {
            return { ...currencyData, currency: countryInfo?.currency };
        }
    }
    return null;
};

const lookupRecommendation = async (origin: CityPlace, recommendation: string): Promise<PlaceWithExists> => {
    const destination = iataToCity[recommendation];

    return {
        place: destination,
        pageExists:
            flightOdCombinationsLookup[`${origin.countryPath}-${origin.cityPath}_${destination.countryPath}-${destination.cityPath}`] ??
            false,
    };
};

const lookupRecommendations = async (origin: CityPlace, destination: CityPlace) => {
    const recommendations = flightRecommendations[`${origin.iata}-${destination.iata}`];

    if (!recommendations) {
        // TODO when all internal linking are completed, throw an error here
        //      now just render the old content instead
        return { cheaper: [], distance: [], budget: [] };
    }

    const cheaper1City = await lookupRecommendation(origin, recommendations.cheaper1);
    const cheaper2City = await lookupRecommendation(origin, recommendations.cheaper2);
    const cheaper3City = await lookupRecommendation(origin, recommendations.cheaper3);
    const distance1City = await lookupRecommendation(origin, recommendations.distance1);
    const distance2City = await lookupRecommendation(origin, recommendations.distance2);
    const distance3City = await lookupRecommendation(origin, recommendations.distance3);
    const budget1City = await lookupRecommendation(origin, recommendations.budget1);
    const budget2City = await lookupRecommendation(origin, recommendations.budget2);
    const budget3City = await lookupRecommendation(origin, recommendations.budget3);

    const cheaper = [cheaper1City, cheaper2City, cheaper3City];
    const distance = [distance1City, distance2City, distance3City];
    const budget = [budget1City, budget2City, budget3City];

    return { cheaper, distance, budget };
};

const fixParams = (params) => {
    const countryLevel1 = validSeoPath(params.countryLevel1);
    const cityLevel1 = validSeoPath(params.cityLevel1);
    const countryLevel2 = validSeoPath(params.countryLevel2);
    const cityLevel2 = validSeoPath(params.cityLevel2);

    return { countryLevel1, cityLevel1, countryLevel2, cityLevel2 };
};

export const getStaticPaths = async () => {
    const paths = flightOdCombinations.map((page) => ({
        params: {
            countryLevel1: page.originCountry,
            cityLevel1: page.originPlace,
            countryLevel2: page.destinationCountry,
            cityLevel2: page.destinationPlace,
        },
    }));

    return {
        paths,
        // TODO remove this when the prod database has all the entries that the dev database has.
        // I.e. the build pregenerates pages for the entries in the prod database and for entries
        // only in the dev database it will generate them on the fly with fallback set to `blocking`.
        fallback: 'blocking',
    };
};

export const getStaticProps = async (context) => {
    const { countryLevel1, cityLevel1, countryLevel2, cityLevel2 } = fixParams(context.params);
    const page = await pageFor(pageContentUrl.forOriginDestination(countryLevel1, cityLevel1, countryLevel2, cityLevel2));

    const origin = flightCities.find((entry: CityPlace) => entry.cityPath === cityLevel1 && entry.countryPath === countryLevel1);
    const destination = flightCities.find((entry: CityPlace) => entry.cityPath === cityLevel2 && entry.countryPath === countryLevel2);
    const destinationWithExists = {
        place: destination,
        pageExists: true,
    };
    const destinationCountry = flightCountries.find((entry: CountryPlace) => entry.countryPath === countryLevel2);

    if (!page.htmlContent && (!destination || !destinationCountry)) {
        return {
            notFound: true,
        };
    }

    const { cheaper, distance, budget } = await lookupRecommendations(origin, destination);
    const prices = await fetchPrices(origin, [...cheaper, destinationWithExists]);

    const ranking = await fetchTrend(destination.iata);
    const currencyInfo: CurrencyInfo = await fetchCurrencyAndRate(countryLevel2 === 'norge' ? countryLevel1 : countryLevel2);

    const updatedCheaper = updatePrices(cheaper, prices);
    const updatedDestination = updatePrices([destinationWithExists], prices)[0].place;

    const popularCities = (flightMapping[destinationCountry.id] ?? []).filter((mapped) => mapped.city !== destination.city).slice(0, 11);
    const popularCountries = getCountriesForDestination(destinationCountry, flightCountries);

    return {
        props: {
            origin,
            destination: { ...updatedDestination, continent: destinationCountry.continent },
            content: page.htmlContent,
            cheaper: updatedCheaper,
            distance,
            budget,
            popularCities,
            popularCountries,
            currencyInfo,
            countryLevel1,
            countryLevel2,
            ranking,
            config: feConfig,
        },
        revalidate: 3600,
    };
};

const CityLevel2 = ({
    origin,
    destination,
    content,
    cheaper,
    distance,
    budget,
    popularCities,
    popularCountries,
    currencyInfo,
    countryLevel1,
    countryLevel2,
    ranking,
    config,
}) => {
    const factory = cityLevel2Factory(origin, destination, flightData, flightSearchFactory());
    const updated = new Date().toLocaleDateString('nb-NO', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    const percent = ranking.percentChange ?? 0;
    const formattedPercent = `${percent < 0 ? '- ' : '+ '}${Math.abs(percent)}%`;

    const showCurrencyInfo = countryLevel2 !== countryLevel1 || (countryLevel2 === countryLevel1 && countryLevel1 !== 'norge');
    const currencyCity = countryLevel2 === 'norge' ? origin.city : destination.city;
    const { unit, currency, rate, name } = currencyInfo ?? {};
    const formattedRate = rate?.toFixed(2) ?? null;

    return (
        <ConfigProvider config={config}>
            <OriginDestinationPage factory={factory} vertical={verticals.flight} data={flightData}>
                <h1 className="text-28 mt-64 mb-24">
                    Fly fra {origin.city} til {destination.city}
                    {destination.price ? ` fra ${destination.price} kr` : null}
                </h1>
                <div className="mb-48" dangerouslySetInnerHTML={{ __html: content }} />

                {(destination.price !== null || ranking.percentChange !== null || (currencyInfo !== null && showCurrencyInfo)) && (
                    <div className="t3 mb-24">Nyttig informasjon om reisen</div>
                )}
                <div className="flex gap-12 flex-col md:flex-row mb-8">
                    {destination.price !== null && (
                        <InfoBox
                            title={'Rimeligste flybilletter 🏷'}
                            main={`fra ${destination.price} kr`}
                            description={`Den beste prisen på en flyvning fra ${origin.city} til ${destination.city} koster fra ${destination.price} kr.`}
                            updated={`Sist oppdatert: ${updated}`}
                        />
                    )}
                    {ranking.percentChange !== null && (
                        <InfoBox
                            title={'Popularitet 📊'}
                            main={`${formattedPercent}`}
                            description={
                                <>
                                    Søkeinteressen for fly til {destination.city} er
                                    <strong className="block">{formattedPercent} siste 30 dager</strong>
                                </>
                            }
                            updated={`Sist oppdatert: ${updated}`}
                        />
                    )}
                    {currencyInfo !== null && showCurrencyInfo && (
                        <InfoBox
                            title={'Valutakurs 💸'}
                            main={`${unit} ${currency} = ${formattedRate} kr`}
                            description={`Valutaen i ${currencyCity} er ${name} (${currency}). ${unit} ${name} er per i dag ca. ${formattedRate} norske kroner (NOK)`}
                            updated={`Sist oppdatert: ${updated}`}
                        />
                    )}
                </div>
                {/* TODO remove this check when all internal links are completed */}
                {cheaper && cheaper.length > 0 ? (
                    <div className="mb-64">
                        <Recommendations
                            origin={origin}
                            destinations={cheaper}
                            showPrices
                            title="Flybilletter som vanligvis er litt billigere"
                            Icon={IconPinMarker24}
                        />
                        <Recommendations
                            origin={origin}
                            destinations={distance}
                            title="Tilsvarende steder med omtrent lik flytid"
                            Icon={IconPlaneTicket24}
                        />
                        <Recommendations
                            origin={origin}
                            destinations={budget}
                            title="Lignende steder - men ofte rimeligere å leve"
                            Icon={IconMap24}
                        />
                    </div>
                ) : (
                    <>
                        <PopularCities
                            destination={destination}
                            cities={popularCities}
                            cdnUrl={flightData.cdnPath}
                            verticalUrl={flightData.basePath}
                            lang={flightData.lang}
                        />
                        <PopularCountries
                            destination={destination}
                            countries={popularCountries}
                            cdnUrl={flightData.cdnPath}
                            verticalUrl={flightData.basePath}
                            lang={flightData.lang}
                        />
                    </>
                )}
                <LinkTree
                    heading={flightData.lang.linkTree.heading}
                    description={flightData.lang.linkTree.description}
                    data={flightData.linkTree}
                    url={flightData.basePath}
                />
            </OriginDestinationPage>
        </ConfigProvider>
    );
};

export default CityLevel2;
